import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
} from '@/components/shared/UISectionWrapper';
import UISelect from '@/components/shared/UISelect';
import UISimpleTable, {
  SimpleTableColumn,
} from '@/components/shared/UISimpleTable';
import UITenantInfo from '@/components/shared/UITenantInfo';
import UIBadge from '@/components/shared/UIBadge';
import { usePayments } from '@/hooks/usePayments';
import {
  PaymentTransaction,
  PaymentDictionariesResponse,
} from '@/types/payments';
import useGetDictionaries from '@/hooks/useGetDictionaries';
import './PaymentsTransactionsSection.scss';
import UIPaginator from '@/components/shared/UIPaginator';
import UIButton from '@/components/shared/UIButton';
import UIIconButton from '@/components/shared/UIIconButton';
import AddPaymentModal from '@/components/dashboard/Payments/AddPaymentModal/AddPaymentModal';
import TransactionDetailsModal from '@/components/dashboard/Payments/TransactionDetailsModal/TransactionDetailsModal';
import { safeToLowerCase } from '@/utils/stringUtils';

interface Transaction {
  id: string;
  date: string;
  category: string;
  tenant: {
    id: number;
    name: string;
    email: string;
    avatar: string | null;
  };
  amount: number;
  status: string;
  unit: {
    id: number;
    name: string;
  };
  property: {
    id: number;
    name: string;
    type?: string;
  };
}

interface AddPaymentData {
  tenantId: string;
  paymentType: string;
  amount: number;
  description?: string;
}

// The component is now fully controlled via props
interface PaymentsTransactionsSectionProps {
  selectedProperty: string;
  selectedTenant: string;
  selectedTimePeriod: string;
  onPropertyChange: (property: string) => void;
  onTenantChange: (tenant: string) => void;
  onTimePeriodChange: (period: string) => void;
  onAddPayment?: (paymentData: AddPaymentData) => void;
}

const PaymentsTransactionsSection: React.FC<
  PaymentsTransactionsSectionProps
> = ({
  selectedProperty,
  selectedTenant,
  selectedTimePeriod,
  onPropertyChange,
  onTenantChange,
  onTimePeriodChange,
  onAddPayment = () => {},
}) => {
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsData, setTransactionsData] = useState<
    PaymentTransaction[]
  >([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const itemsPerPage = 5;
  const { getPaymentsTransactions, loading, error } = usePayments();
  const {
    data: dictionaries,
    isLoading: isDictionariesLoading,
    error: dictionariesError,
  } = useGetDictionaries<PaymentDictionariesResponse>('payments');

  const propertyOptions = useMemo(() => {
    if (!dictionaries?.properties)
      return [{ value: 'all-properties', label: 'All properties' }];
    return [
      { value: 'all-properties', label: 'All properties' },
      ...dictionaries.properties.map((property) => ({
        value: property.id.toString(),
        label: property.name,
      })),
    ];
  }, [dictionaries?.properties]);

  const tenantOptions = useMemo(() => {
    if (!dictionaries?.tenants)
      return [{ value: 'all-tenants', label: 'All tenants' }];
    return [
      { value: 'all-tenants', label: 'All tenants' },
      ...dictionaries.tenants.map((tenant) => ({
        value: tenant.id.toString(),
        label: tenant.name,
      })),
    ];
  }, [dictionaries?.tenants]);

  const timePeriodOptions = useMemo(() => {
    if (!dictionaries?.periods) return [];
    return dictionaries.periods.map((period) => ({
      value: period.value,
      label: period.label,
    }));
  }, [dictionaries?.periods]);

  const loadTransactions = useCallback(async () => {
    setIsDataLoading(true);
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const filters = {
        period: selectedTimePeriod,
        propertyId: selectedProperty,
        tenantId: selectedTenant,
        limit: itemsPerPage,
        offset,
      };
      const data = await getPaymentsTransactions(filters);
      if (data) {
        setTransactionsData(data.payments || []); // Protection against null/undefined
        setTotalTransactions(data.total);
      }
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setIsDataLoading(false);
    }
  }, [
    currentPage,
    getPaymentsTransactions,
    selectedProperty,
    selectedTenant,
    selectedTimePeriod,
  ]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handlePropertyChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onPropertyChange(event.target.value);
    setCurrentPage(1);
  };

  const handleTimePeriodChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onTimePeriodChange(event.target.value);
    setCurrentPage(1);
  };

  const handleTenantChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onTenantChange(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleAddPaymentClick = () => {
    setIsAddPaymentModalOpen(true);
  };

  const handleAddPaymentModalClose = () => {
    setIsAddPaymentModalOpen(false);
  };

  const handleAddPaymentSubmit = (paymentData: AddPaymentData) => {
    onAddPayment(paymentData);
    handleAddPaymentModalClose();
    loadTransactions();
  };

  const handleDetailsClick = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    setIsDetailsModalOpen(true);
  };

  const handleDetailsModalClose = () => {
    setIsDetailsModalOpen(false);
    setSelectedTransactionId(null);
  };

  const getCurrentTransactions = (): Transaction[] => {
    if (!Array.isArray(transactionsData)) return [];
    return transactionsData.map((transaction) => ({
      id: transaction.id,
      date: transaction.date,
      category: transaction.category,
      tenant: {
        id: transaction.tenant?.id || 0,
        name: transaction.tenant?.name || 'Unknown',
        email: transaction.tenant?.email || '',
        avatar: transaction.tenant?.avatar || null,
      },
      amount: transaction.amount,
      status: transaction.status,
      unit: {
        id: transaction.unit?.id || 0,
        name: transaction.unit?.name || 'Unknown Unit',
      },
      property: {
        id: transaction.property?.id || 0,
        name: transaction.property?.name || 'Unknown Property',
        type: transaction.property?.type,
      },
    }));
  };

  const currentTransactions = getCurrentTransactions();
  const totalItems = totalTransactions;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pageInfo = `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems}`;

  const columns: SimpleTableColumn[] = [
    {
      key: 'date',
      label: 'DATE',
      width: '112px',
      headerContent: <span>DATE</span>,
      render: (value, row) => <span>{row.date}</span>,
    },
    {
      key: 'category',
      label: 'CATEGORY',
      width: '166px',
      headerContent: <span>CATEGORY</span>,
      render: (value, row) => <span>{row.category}</span>,
    },
    {
      key: 'tenant',
      label: 'TENANT',
      width: '282px',
      headerContent: <span>TENANT</span>,
      render: (value, row) => (
        <Link
          to={`/dashboard/tenants/${row.tenant.id}`}
          className="PaymentsTransactionsSection__tenant-link"
        >
          <UITenantInfo
            name={row.tenant.name}
            email={row.tenant.email}
            tenantId={row.tenant.id}
            avatar={row.tenant.avatar}
            size="medium"
          />
        </Link>
      ),
    },
    {
      key: 'amount',
      label: 'AMOUNT',
      width: '117px',
      headerContent: <span>AMOUNT</span>,
      render: (value, row) => <span>{`$ ${row.amount.toLocaleString()}`}</span>,
    },
    {
      key: 'status',
      label: 'STATUS',
      width: '120px',
      headerContent: <span>STATUS</span>,
      render: (value, row) => {
        const getStatusBadge = (status: string) => {
          switch (safeToLowerCase(status)) {
            case 'completed':
              return (
                <UIBadge variant="success" shape="default">
                  COMPLETED
                </UIBadge>
              );
            case 'pending':
              return (
                <UIBadge variant="warning" shape="default">
                  PENDING
                </UIBadge>
              );
            case 'failed':
              return (
                <UIBadge variant="danger" shape="default">
                  FAILED
                </UIBadge>
              );
            case 'cancelled':
              return (
                <UIBadge variant="secondary" shape="default">
                  CANCELLED
                </UIBadge>
              );
            case 'processing':
              return (
                <UIBadge variant="info" shape="default">
                  PROCESSING
                </UIBadge>
              );
            default:
              return (
                <UIBadge variant="secondary" shape="default">
                  {status.toUpperCase()}
                </UIBadge>
              );
          }
        };
        return getStatusBadge(row.status);
      },
    },
    {
      key: 'unit',
      label: 'UNIT',
      width: '91px',
      headerContent: <span>UNIT</span>,
      render: (value, row) => (
        <Link
          to={`/dashboard/properties/${row.property.id}/units/${row.unit.id}`}
          className="PaymentsTransactionsSection__unit-link"
        >
          {row.unit.name}
        </Link>
      ),
    },
    {
      key: 'property',
      label: 'PROPERTY',
      width: '200px',
      headerContent: <span>PROPERTY</span>,
      render: (value, row) => (
        <Link
          to={`/dashboard/properties/${row.property.id}`}
          className="PaymentsTransactionsSection__property-link"
        >
          <div className="PaymentsTransactionsSection__property-cell">
            <span className="PaymentsTransactionsSection__property-name">
              {row.property.name}
            </span>
            <UIBadge variant="success" shape="default">
              {(row.property.type || 'No units').toUpperCase()}
            </UIBadge>
          </div>
        </Link>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '48px',
      headerContent: <span />,
      render: (value, row) => (
        <UIIconButton
          iconType="info"
          variant="secondary"
          size="small"
          onClick={() => handleDetailsClick(row.id)}
        />
      ),
    },
  ];

  return (
    <UISectionWrapper spacing="default" align="stretch" shadow="default">
      <UISectionHeader>
        <div className="PaymentsTransactionsSection__header">
          <div className="PaymentsTransactionsSection__title-row">
            <div className="PaymentsTransactionsSection__title-content">
              <h2 className="PaymentsTransactionsSection__title">
                Transactions for
              </h2>
              <UISelect
                size="medium"
                value={selectedProperty}
                onChange={handlePropertyChange}
                options={propertyOptions}
                className="PaymentsTransactionsSection__property-select"
                disabled={isDictionariesLoading || loading || isDataLoading}
              />
              <UISelect
                size="medium"
                value={selectedTenant}
                onChange={handleTenantChange}
                options={tenantOptions}
                className="PaymentsTransactionsSection__tenant-select"
                disabled={isDictionariesLoading || loading || isDataLoading}
              />
              <UISelect
                size="medium"
                value={selectedTimePeriod}
                onChange={handleTimePeriodChange}
                options={timePeriodOptions}
                className="PaymentsTransactionsSection__period-select"
                disabled={isDictionariesLoading || loading || isDataLoading}
              />
            </div>
            <UIButton
              variant="primary"
              size="medium"
              onClick={handleAddPaymentClick}
              disabled={isDictionariesLoading || loading || isDataLoading}
            >
              Add Payment
            </UIButton>
          </div>
          <div className="PaymentsTransactionsSection__separator"></div>
        </div>
      </UISectionHeader>

      <UISectionBody>
        <div className="PaymentsTransactionsSection__content">
          {(isDictionariesLoading || loading || isDataLoading) && (
            <div className="PaymentsTransactionsSection__loading">
              Loading...
            </div>
          )}
          {(error || dictionariesError) && (
            <div className="PaymentsTransactionsSection__error">
              Error loading data.
            </div>
          )}
          {!isDictionariesLoading &&
            !loading &&
            !error &&
            !dictionariesError && (
              <>
                <UISimpleTable
                  data={currentTransactions}
                  columns={columns}
                  className="PaymentsTransactionsSection__table"
                  getRowKey={(row) => row.id}
                />
                {totalPages > 1 && (
                  <div className="PaymentsTransactionsSection__pagination">
                    <div className="PaymentsTransactionsSection__page-info">
                      {pageInfo}
                    </div>
                    <UIPaginator
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      className="PaymentsTransactionsSection__paginator"
                    />
                  </div>
                )}
                {currentTransactions.length === 0 && (
                  <div className="PaymentsTransactionsSection__no-data">
                    No transactions found.
                  </div>
                )}
              </>
            )}
        </div>
      </UISectionBody>

      <AddPaymentModal
        isOpen={isAddPaymentModalOpen}
        onClose={handleAddPaymentModalClose}
        onSubmit={handleAddPaymentSubmit}
      />

      <TransactionDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={handleDetailsModalClose}
        transactionId={selectedTransactionId}
      />
    </UISectionWrapper>
  );
};

export default PaymentsTransactionsSection;